import _ from 'lodash';
import { Tooltip } from 'design-system';
import classNames from 'classnames';
import ColorHash from 'color-hash';
import INotification from '@scale/shared/types/notifications/notification';
import INotificationSend from '@scale/shared/types/notifications/notificationSend';
import moment from 'moment';
import React, { SyntheticEvent } from 'react';

const colorHash = new ColorHash();
const pickColorBasedOnBg = ([red, green, blue]: number[]) =>
  red * 0.299 + green * 0.587 + blue * 0.114 > 186 ? '#000' : '#fff';

const Notification: React.FC<{
  notificationObject: INotificationSend & {
    notification: INotification;
  };
  disabled: boolean;
  dismissNotification: (notificationId: string) => void;
}> = ({
  disabled,
  notificationObject: { notification, dismissedAt, _id, createdAt },
  dismissNotification,
}) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [toggleDescription, setToggleDescription] = React.useState<boolean>(false);
  const sourceColor = colorHash.rgb(notification.source);
  const dismissed = !_.isUndefined(dismissedAt);

  const onToggleDescription = React.useCallback(() => {
    setToggleDescription(value => !value);
  }, [setToggleDescription]);

  const onDismissNotification = async (e: SyntheticEvent) => {
    e.stopPropagation();
    try {
      setSubmitting(true);
      await dismissNotification(_id as string);
    } finally {
      setSubmitting(false);
    }
  };

  const descriptionLines = notification.description
    ?.split('\n')
    .map((line, index) => <p key={index}>{line}</p>);

  return (
    <div className={classNames('notification', { dismissed })} onClick={onToggleDescription}>
      <div className="notification__header">
        <div className="notification__header-title">
          <h4>{notification.title}</h4>
        </div>
        <div className="notification__header-info">
          <span className="notification__header-date">
            {moment(createdAt).format('MMM D, HH:mm')}
          </span>
          <Tooltip title={`This notification was sent from ${notification.source}`} placement="top">
            <span
              style={{
                color: pickColorBasedOnBg(sourceColor),
                backgroundColor: `rgb(${sourceColor[0]}, ${sourceColor[1]}, ${sourceColor[2]})`,
              }}
              className="notification__source"
            >
              {notification.source}
            </span>
          </Tooltip>
        </div>
      </div>
      <div className="notification__body">
        {notification.description && (
          <div className={classNames('notification__description', { toggleDescription })}>
            {descriptionLines}
          </div>
        )}
        <div className="notification__body-btns">
          {!dismissedAt && (
            <button disabled={submitting || disabled} onClick={onDismissNotification}>
              Mark as read
            </button>
          )}
          {notification.actionUrl && (
            <a href={notification.actionUrl} target="__blank">
              Take action
            </a>
          )}
          {notification.helpUrl && (
            <a href={notification.helpUrl} target="__blank">
              Tell me more
            </a>
          )}
        </div>
      </div>
      <style jsx>{`
        .notification {
          position: relative;
          background: #fff;
          cursor: pointer;
          border-radius: 3px;
          padding: 8px;
          margin-bottom: 16px;
          border: 1px solid var(--color-RemoGray10);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .notification.dismissed {
          background: var(--color-RemoGray10);
        }
        .notification:last-child {
          margin-bottom: 0;
        }
        .notification__actions {
          display: none;
          position: absolute;
          border-radius: 3px;
          width: 160px;
          right: 12px;
          top: 30px;
          background: #fff;
          flex-direction: column;
          align-items: center;
          padding: 8px 8px 4px;
          // above all notifs
          z-index: 1003;
        }
        .notification__actions.toggled {
          display: flex;
        }
        .notification__actions a {
          color: #fff;
          text-decoration: none;
        }
        .notification__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .notification__header-title {
          display: flex;
          padding: 3px;
          align-items: flex-start;
        }
        .notification__header h4 {
          line-height: 1.2;
          margin: 0;
        }
        .notification__header-info {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }
        .notification__header-date {
          color: var(--color-RemoGray50);
          min-width: 95px;
          margin: 0 8px;
          line-height: 1.2;
        }
        .notification__body {
          margin: 8px 0 0 4px;
        }
        .notification__source {
          background: #000;
          color: #fff;
          border-radius: 8px;
          font-size: 12px;
          line-height: 1;
          margin-right: 5px;
          text-align: center;
          padding: 3px 8px;
        }
        .notification__body span {
          display: block;
          line-height: 1.4;
        }
        .notification__body p {
        }

        .notification__description {
          margin: 0;
          margin-top: 8px;
          line-height: 1.5;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 1; /* number of lines to show */
          line-clamp: 1;
          -webkit-box-orient: vertical;
        }

        .notification__body .notification__description.toggleDescription {
          display: block;
        }

        .notification__body-btns {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-top: 16px;
        }

        .notification__body-btns a {
          color: #fff;
          text-decoration: none;
          font-size: 10px;
          border-radius: 3px;
          display: block;
          background: var(--color-RemoBlue);
          line-height: 1;
          padding: 8px;
        }

        .notification__body-btns a:nth-child(2) {
          margin: 0 8px 0;
        }

        .notification__body-btns button {
          color: #000;
          cursor: pointer;
          margin: 0;
          width: auto;
          line-height: 1;
          border-radius: 3px;
          font-weight: bold;
          padding: 8px;
          border: none;
          display: block;
          font-size: 10px;
          text-decoration: none;
        }
      `}</style>
    </div>
  );
};

export default Notification;
